import React, { useState, useEffect } from 'react';
import { X, ChevronRight, Shield, Lock, Mail, Eye, EyeOff, RefreshCw } from 'lucide-react';
import { ElementalLogo } from './ElementalLogo';
import { LegalModal } from './LegalModal';

// --- SPLASH SCREEN ---
export const Splash = ({ onFinish }) => {
    useEffect(() => {
        const timer = setTimeout(() => {
            onFinish();
        }, 2500);
        return () => clearTimeout(timer);
    }, [onFinish]);

    return (
        <div className="fixed inset-0 z-[100] bg-slate-950 flex flex-col items-center justify-center animate-fade-out px-4">
            <div className="animate-bounce-slow">
                <ElementalLogo className="w-24 h-24 text-white" />
            </div>
            <h1 className="mt-6 text-3xl font-bold text-white tracking-widest uppercase text-center">
                Four Elements Finance&trade;
            </h1>
            <p className="text-sm text-slate-400 mt-2 font-medium tracking-wide text-center">
                Master all four for financial freedom
            </p>
        </div>
    );
};

// --- ONBOARDING SLIDES ---
export const Onboarding = ({ onComplete }) => {
    const [step, setStep] = useState(0);
    const slides = [
        {
            title: "The Philosophy",
            desc: "Wealth isn't just numbers. It's a balance of four forces.",
            icon: <ElementalLogo className="w-16 h-16 text-white" />
        },
        {
            title: "💨 Air (Vision)",
            desc: "Your long-term trajectory. Net Worth, Retirement, and Future Goals.",
            icon: <div className="text-cyan-400 text-6xl">💨</div>
        },
        {
            title: "⛰️ Earth (Assets)",
            desc: "Your foundation. Cash, Investments, and Property that build stability.",
            icon: <div className="text-emerald-400 text-6xl">⛰️</div>
        },
        {
            title: "💧 Water (Inflow)",
            desc: "The source. Salary, Side Hustles, and the Reservoirs (Savings) you fill.",
            icon: <div className="text-blue-400 text-6xl">💧</div>
        },
        {
            title: "🔥 Fire (Action)",
            desc: "The burn. Expenses, Debt, and the daily Discipline to keep the flame controlled.",
            icon: <div className="text-orange-400 text-6xl">🔥</div>
        }
    ];

    const handleNext = () => {
        if (step < slides.length - 1) setStep(step + 1);
        else onComplete();
    };

    return (
        <div className="fixed inset-0 z-50 bg-slate-900/90 backdrop-blur-md flex items-center justify-center p-4">
            <div className="bg-slate-950 border border-slate-800 p-8 rounded-2xl max-w-md w-full text-center shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 left-0 h-1 bg-slate-800 w-full">
                    <div className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-500" style={{ width: `${((step + 1) / slides.length) * 100}%` }}></div>
                </div>

                <div className="mb-8 mt-4 flex justify-center h-24 items-center">
                    {slides[step].icon}
                </div>
                
                <h2 className="text-2xl font-bold text-white mb-2">{slides[step].title}</h2>
                <p className="text-slate-400 mb-8 leading-relaxed">{slides[step].desc}</p>

                <button onClick={handleNext} className="w-full py-3 bg-white text-slate-900 font-bold rounded-xl hover:scale-105 transition-transform flex items-center justify-center gap-2">
                    {step === slides.length - 1 ? "Enter System" : "Next"} <ChevronRight className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
};

// --- AUTH MODAL ---
export const AuthModal = ({ onClose, onLogin, onRegister, onReset }) => {
    const [isRegistering, setIsRegistering] = useState(false);
    const [isResetting, setIsResetting] = useState(false); 
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState(""); 
    const [showPassword, setShowPassword] = useState(false);
    
    // --- TERMS STATE ---
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [showTerms, setShowTerms] = useState(false);

    const handleSubmit = (e) => {
        e.preventDefault();
        
        if (isResetting) {
            onReset(email);
            return;
        }

        if (isRegistering) {
            if (password !== confirmPassword) {
                alert("Passwords do not match.");
                return;
            }
            if (!agreedToTerms) {
                alert("You must agree to the Terms of Service to continue.");
                return;
            }
            onRegister(email, password);
        } else {
            onLogin(email, password);
        }
    };

    return (
        <>
            {showTerms && <LegalModal onClose={() => setShowTerms(false)} />}
            
            <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
                <div className="bg-slate-900 border border-slate-700 w-full max-w-md rounded-2xl shadow-2xl p-6 relative">
                    <button onClick={onClose} className="absolute top-4 right-4 text-slate-500 hover:text-white transition-colors">
                        <X className="w-5 h-5" />
                    </button>

                    <div className="text-center mb-6">
                        <div className="inline-block p-3 bg-slate-800 rounded-full mb-3">
                            {isResetting ? <RefreshCw className="w-8 h-8 text-blue-400" /> : <Shield className="w-8 h-8 text-emerald-400" />}
                        </div>
                        <h2 className="text-2xl font-bold text-white">
                            {isResetting ? "Reset Password" : isRegistering ? "Create Account" : "Welcome Back"}
                        </h2>
                        <p className="text-slate-400 text-xs mt-1">
                            {isResetting ? "We'll send a link to your inbox." : isRegistering ? "Secure your data in the cloud." : "Login to sync your financial elements."}
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-1 ml-1">Email</label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-2.5 w-4 h-4 text-slate-500" />
                                <input 
                                    type="email" 
                                    required 
                                    value={email} 
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full bg-slate-900 border border-slate-700 rounded-lg py-2 pl-10 pr-4 text-white outline-none focus:border-emerald-500 transition-colors"
                                    placeholder="name@example.com"
                                />
                            </div>
                        </div>

                        {!isResetting && (
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1 ml-1">Password</label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-2.5 w-4 h-4 text-slate-500" />
                                    <input 
                                        type={showPassword ? "text" : "password"} 
                                        required 
                                        value={password} 
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full bg-slate-900 border border-slate-700 rounded-lg py-2 pl-10 pr-10 text-white outline-none focus:border-emerald-500 transition-colors"
                                        placeholder="••••••••"
                                    />
                                    <button 
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-2.5 text-slate-500 hover:text-white"
                                    >
                                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                                
                                {!isRegistering && (
                                    <div className="text-right mt-1">
                                        <button type="button" onClick={() => setIsResetting(true)} className="text-[10px] text-slate-400 hover:text-blue-400">Forgot Password?</button>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* CONFIRM PASSWORD & TERMS (Register Only) */}
                        {!isResetting && isRegistering && (
                            <div className="animate-fade-in space-y-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1 ml-1">Confirm Password</label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-2.5 w-4 h-4 text-slate-500" />
                                        <input 
                                            type={showPassword ? "text" : "password"} 
                                            required 
                                            value={confirmPassword} 
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            className={`w-full bg-slate-900 border rounded-lg py-2 pl-10 pr-4 text-white outline-none transition-colors ${
                                                confirmPassword && confirmPassword !== password 
                                                ? 'border-red-500 focus:border-red-500' 
                                                : 'border-slate-700 focus:border-emerald-500'
                                            }`}
                                            placeholder="••••••••"
                                        />
                                    </div>
                                </div>

                                <div className="mt-2 p-2 bg-slate-800/50 rounded text-[10px] text-slate-400 space-y-1">
                                    <p className="font-bold text-slate-300">Requirements:</p>
                                    <div className="flex items-center gap-1"><div className={`w-1.5 h-1.5 rounded-full ${password.length >= 8 ? 'bg-emerald-500' : 'bg-slate-600'}`}></div><span>8+ Characters</span></div>
                                    <div className="flex items-center gap-1"><div className={`w-1.5 h-1.5 rounded-full ${/[A-Za-z]/.test(password) && /\d/.test(password) ? 'bg-emerald-500' : 'bg-slate-600'}`}></div><span>Letter & Number</span></div>
                                    <div className="flex items-center gap-1"><div className={`w-1.5 h-1.5 rounded-full ${/[\W_]/.test(password) ? 'bg-emerald-500' : 'bg-slate-600'}`}></div><span>Special Character</span></div>
                                </div>

                                {/* TERMS CHECKBOX */}
                                <div className="flex items-start gap-2 pt-2 border-t border-slate-800">
                                    <input 
                                        type="checkbox" 
                                        id="terms" 
                                        checked={agreedToTerms} 
                                        onChange={(e) => setAgreedToTerms(e.target.checked)}
                                        className="mt-0.5 bg-slate-900 border-slate-600 rounded accent-emerald-500"
                                    />
                                    <label htmlFor="terms" className="text-xs text-slate-400">
                                        I agree to the <button type="button" onClick={() => setShowTerms(true)} className="text-emerald-400 hover:underline font-bold">Terms of Service & Disclaimer</button>. I understand this app provides information, not financial advice.
                                    </label>
                                </div>
                            </div>
                        )}

                        <button 
                            type="submit" 
                            disabled={isRegistering && !agreedToTerms}
                            className={`w-full font-bold py-3 rounded-lg transition-colors mt-4 ${
                                isResetting ? 'bg-blue-600 hover:bg-blue-500 text-white' : 
                                (isRegistering && !agreedToTerms) ? 'bg-slate-700 text-slate-500 cursor-not-allowed' : 'bg-emerald-500 hover:bg-emerald-400 text-slate-900'
                            }`}
                        >
                            {isResetting ? "Send Reset Link" : isRegistering ? "Create Account" : "Log In"}
                        </button>
                    </form>

                    <div className="mt-4 text-center">
                        {isResetting ? (
                            <button onClick={() => setIsResetting(false)} className="text-xs text-slate-400 hover:text-white underline">Back to Login</button>
                        ) : (
                            <button onClick={() => { setIsRegistering(!isRegistering); setConfirmPassword(''); }} className="text-xs text-slate-400 hover:text-white underline">
                                {isRegistering ? "Already have an account? Log In" : "New user? Create Account"}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};